import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { RotateCcw, Search, Sparkles } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAppData } from '../context/AppDataContext';
import NumberInput from '../components/NumberInput';
import { shortenTitle } from '../components/book/TitleEditor';
import { RECORD_TYPES } from '../utils/recordUtils';
import { getPageScale } from '../utils/pageScale';
import { BOOK_STATUS, getBookStatus } from '../constants/bookStatus';
import { cleanSharedQuote, findSharedBook } from '../utils/shareText';
import {
  readSharedPayload, discardSharedPayload, loadShareDraft, saveShareDraft, clearShareDraft
} from '../utils/shareInbox';
import { readLastRecordBook, rememberRecordBook } from '../utils/recordDrafts';
import { loadBookSnapshot } from '../utils/sessionCache';
import { toGeminiImage } from '../utils/imageData';
import { getGeminiApiKey, readQuoteFromImage } from '../services/geminiService';

const isReading = (book) => !!book && getBookStatus(book) === BOOK_STATUS.READING;
const titlesOf = (book) => (book ? [book.title, shortenTitle(book.title)] : []);
const MAX_SEARCH_RESULTS = 8;

/**
 * 공유받은 문장 — 안드로이드 공유 목록에서 북러닝을 고르면 오는 화면.
 *
 * 받은 글을 인용으로 다듬어 채우고, 제목이 든 책(없으면 마지막으로 기록한 책)을 미리 고른다.
 * 이미지만 왔으면 Gemini 로 카드 속 문장을 읽는다. 저장하면 창을 닫아 보던 앱으로 돌려보낸다.
 * 고치던 내용은 초안으로 남겨, 쪽수를 확인하러 다녀오는 사이 앱이 꺼져도 이어서 쓴다.
 */
const ShareReceive = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { books: loadedBooks, loading, addRecord, recordsByBook } = useAppData();
  // 로그인 확인과 데이터가 오기 전에는 지난번 책 목록 사본으로 고른다. 공유로 열 때
  // 그 확인(서버 왕복)을 기다리지 않고 바로 그리기 위해서다 (utils/sessionCache, App.jsx)
  const [snapshotBooks] = useState(loadBookSnapshot);
  const books = loading ? snapshotBooks : loadedBooks;

  const [payload, setPayload] = useState(undefined); // undefined = 읽는 중, null = 받은 것 없음
  const [initialized, setInitialized] = useState(false);
  const [bookId, setBookId] = useState(null);
  const [quote, setQuote] = useState('');
  const [autoQuote, setAutoQuote] = useState(''); // 자동으로 다듬어 넣은 문장 — 손대지 않았는지 알기 위해
  const [page, setPage] = useState('');
  const [thought, setThought] = useState('');
  const [bookQuery, setBookQuery] = useState('');
  const [ocr, setOcr] = useState({ status: 'idle', message: '' });
  const [saving, setSaving] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);
  // 공유로 새로 열린 창이면 저장한 뒤 닫아서 보던 앱으로 돌아갈 수 있다
  const [closable] = useState(() => window.history.length <= 1);

  const query = searchParams.toString();
  // 어느 공유에 대한 초안인지
  const source = useMemo(() => {
    const params = new URLSearchParams(query);
    if (params.get('id')) return `id:${params.get('id')}`;
    const text = params.get('text') || params.get('title') || '';
    return text ? `text:${text.slice(0, 200)}` : '';
  }, [query]);

  // 받은 것 꺼내기 — 앱이 열린 채 새로 공유하면 주소가 바뀌어 처음부터 다시 채운다
  useEffect(() => {
    let alive = true;
    setPayload(undefined);
    setInitialized(false);
    setOcr({ status: 'idle', message: '' });
    readSharedPayload(new URLSearchParams(query))
      .then(result => { if (alive) setPayload(result); })
      .catch(() => {
        if (alive) setPayload({ title: '', text: '', url: '', image: null, droppedImage: false, error: 'receive' });
      });
    return () => { alive = false; };
  }, [query]);

  useEffect(() => {
    if (!payload?.image) {
      setImageUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(payload.image);
    setImageUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [payload]);

  // 처음 한 번 채우기 — 책 목록(사본이라도)이 있어야 어느 책인지 고를 수 있다
  useEffect(() => {
    if (initialized || payload === undefined || (loading && snapshotBooks.length === 0)) return;
    if (!payload) {
      setInitialized(true);
      return;
    }

    const draft = loadShareDraft(source);
    if (draft) {
      setBookId(draft.bookId || null);
      setQuote(draft.quote || '');
      setAutoQuote(draft.autoQuote || '');
      setPage(draft.page || '');
      setThought(draft.thought || '');
    } else {
      const lastBook = books.find(b => b.id === readLastRecordBook());
      const readingBooks = books.filter(isReading);
      const pick = findSharedBook(books, `${payload.title}\n${payload.text}`, shortenTitle, isReading)
        || (isReading(lastBook) ? lastBook : null)
        || (readingBooks.length === 1 ? readingBooks[0] : null);
      const cleaned = cleanSharedQuote(payload.text, { titles: titlesOf(pick) });
      setBookId(pick?.id || null);
      setQuote(cleaned.quote);
      setAutoQuote(cleaned.quote);
      setPage(cleaned.page);
      setThought('');
    }
    setInitialized(true);
  }, [initialized, payload, loading, books, snapshotBooks, source]);

  useEffect(() => {
    if (!initialized || !source || !payload) return;
    saveShareDraft({ source, bookId, quote, autoQuote, page, thought });
  }, [initialized, source, payload, bookId, quote, autoQuote, page, thought]);

  const book = books.find(b => b.id === bookId) || null;
  const scale = getPageScale(book);
  const hasGeminiKey = !!getGeminiApiKey();

  const lastPage = useMemo(() => {
    if (!book) return null;
    const pages = (recordsByBook.get(book.id) || [])
      .map(r => r.page)
      .filter(p => typeof p === 'number' && p > 0);
    return pages.length ? getPageScale(book).toDisplay(Math.max(...pages)) : null;
  }, [book, recordsByBook]);

  const candidates = useMemo(() => {
    const q = bookQuery.trim().toLowerCase();
    if (q) {
      return books
        .filter(b => b.title?.toLowerCase().includes(q) || b.author?.toLowerCase().includes(q))
        .slice(0, MAX_SEARCH_RESULTS);
    }
    const list = [book, ...books.filter(isReading)].filter(Boolean);
    return list.filter((b, i) => list.findIndex(other => other.id === b.id) === i);
  }, [books, book, bookQuery]);

  /** 이미지 속 문장 읽기. 직접 누른 경우(force)에는 손댄 문장이 있으면 바꿀지 묻는다 */
  const readImage = async ({ force = false } = {}) => {
    if (!payload?.image || ocr.status === 'reading') return;
    if (force && quote.trim() && quote !== autoQuote
      && !window.confirm('지금 적힌 문장을 이미지에서 읽은 문장으로 바꿀까요?')) return;

    setOcr({ status: 'reading', message: '' });
    try {
      const result = await readQuoteFromImage(await toGeminiImage(payload.image));
      if (result.quote) {
        setQuote(current => (force || !current.trim() ? result.quote : current));
        setAutoQuote(result.quote);
      }
      if (!bookId && (result.title || result.author)) {
        const found = findSharedBook(books, `${result.title}\n${result.author}`, shortenTitle, isReading);
        if (found) setBookId(found.id);
      }
      if (result.page) setPage(current => current || result.page);
      setOcr(result.quote
        ? { status: 'done', message: '' }
        : { status: 'error', message: '이미지에서 문장을 찾지 못했어요.' });
    } catch (error) {
      setOcr({ status: 'error', message: error.message || '이미지를 읽지 못했어요.' });
    }
  };

  // 글 없이 이미지만 왔으면 바로 읽는다
  useEffect(() => {
    if (initialized && payload?.image && !quote.trim() && ocr.status === 'idle' && hasGeminiKey) {
      readImage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialized, payload]);

  const chooseBook = (next) => {
    setBookId(next.id);
    setBookQuery('');
    // 손대지 않은 문장이면 새로 고른 책의 제목으로 출처 줄을 다시 떼어 낸다
    if (payload?.text && quote === autoQuote) {
      const cleaned = cleanSharedQuote(payload.text, { titles: titlesOf(next) });
      setQuote(cleaned.quote);
      setAutoQuote(cleaned.quote);
      if (!page && cleaned.page) setPage(cleaned.page);
    }
  };

  /** 다 쓴 공유를 정리하고, 공유로 열린 창이면 닫아서 보던 앱으로 돌아간다 */
  const finish = async (to, closeDelay) => {
    clearShareDraft();
    await discardSharedPayload();
    navigate(to, { replace: true });
    if (closable) setTimeout(() => window.close(), closeDelay);
  };

  const handleSave = async () => {
    const text = quote.trim();
    if (!book || !text || saving) return;

    setSaving(true);
    try {
      const pageNumber = parseInt(page, 10);
      const canonicalPage = Number.isFinite(pageNumber) && pageNumber > 0 ? scale.toCanonical(pageNumber) : null;
      const recordId = await addRecord({
        bookId: book.id, type: RECORD_TYPES.QUOTE, text, page: canonicalPage, parentId: null
      });

      const extra = thought.trim();
      if (extra) {
        if (recordId) {
          await addRecord({
            bookId: book.id, type: RECORD_TYPES.THOUGHT, text: extra, page: canonicalPage, parentId: recordId
          });
        } else {
          toast.error('인용은 저장했지만 생각을 붙이지 못했어요');
        }
      }

      rememberRecordBook(book.id);
      toast.success('인용을 남겼어요');
      // 닫히지 않으면 방금 남긴 인용이 보이는 기록 화면에 남는다
      await finish(`/book/${book.id}?tab=records`, 900);
    } catch (error) {
      toast.error(error.message || '기록에 실패했어요');
      setSaving(false);
    }
  };

  const handleCancel = () => finish('/', 200);

  if (payload === undefined || (payload && !initialized)) {
    return <p className="label py-20 text-center">LOADING</p>;
  }

  const hasContent = payload && (payload.text || payload.title || payload.image);

  if (!hasContent) {
    return (
      <div className="mx-auto max-w-2xl py-16 text-center">
        <p className="font-display text-2xl">
          {payload?.error ? '공유받은 내용을 읽지 못했어요' : '공유받은 문장이 없어요'}
        </p>
        <p className="mt-3 text-sm leading-relaxed text-ink-600 dark:text-ink-300 font-sans">
          {payload?.error
            ? '전자책 앱에서 한 번 더 공유해 주세요.'
            : '전자책 앱에서 문장을 고르고 공유 → 북러닝을 누르면 여기로 와요.'}
        </p>
        <Link to="/" className="btn-ghost mt-6 text-xs">홈으로</Link>
      </div>
    );
  }

  const canSave = !!book && !!quote.trim() && !saving;

  return (
    <div className="mx-auto max-w-2xl">
      <p className="label">SHARED</p>
      <h1 className="mt-5 font-display text-3xl sm:text-4xl">공유받은 문장</h1>
      {payload.droppedImage && (
        <p className="mt-3 text-xs leading-relaxed text-ink-600 dark:text-ink-300 font-sans">
          함께 온 이미지는 받지 못했어요. 앱을 한 번 연 뒤 다시 공유하면 이미지도 받을 수 있어요.
        </p>
      )}

      {/* 책 */}
      <section className="mt-8">
        <span className="label">책</span>
        {!book && (
          <p className="mt-2 text-sm text-ink-600 dark:text-ink-300 font-sans">어느 책의 문장인지 골라주세요</p>
        )}
        <div className="mt-3 flex flex-wrap gap-2">
          {candidates.map(item => (
            <button
              key={item.id}
              type="button"
              onClick={() => chooseBook(item)}
              aria-pressed={item.id === bookId}
              title={item.title}
              className={`chip max-w-full ${item.id === bookId ? 'chip-active' : ''}`}
            >
              <span className="min-w-0 truncate">{shortenTitle(item.title) || item.title}</span>
            </button>
          ))}
        </div>
        <div className="relative mt-3">
          <Search
            size={14}
            strokeWidth={1.5}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-500 dark:text-ink-400 pointer-events-none"
          />
          <input
            type="text"
            value={bookQuery}
            onChange={e => setBookQuery(e.target.value)}
            placeholder="다른 책 찾기"
            className="field !pl-8 !py-2 text-sm"
          />
        </div>
      </section>

      {/* 받은 이미지 */}
      {imageUrl && (
        <section className="mt-8 flex items-start gap-4">
          <img
            src={imageUrl}
            alt="공유받은 문장 카드"
            className="w-24 shrink-0 rounded-sm border border-ink-200 dark:border-ink-800 object-cover"
          />
          <div className="min-w-0 flex-1 text-sm leading-relaxed text-ink-600 dark:text-ink-300 font-sans">
            {!hasGeminiKey ? (
              <p>
                이미지 속 문장을 읽으려면{' '}
                <Link to="/settings" className="underline underline-offset-2">설정</Link>
                에서 Gemini API 키를 등록해 주세요. 아래에 직접 적어도 됩니다.
              </p>
            ) : ocr.status === 'reading' ? (
              <p>이미지에서 문장을 읽는 중…</p>
            ) : (
              <>
                {ocr.status === 'error' && <p>{ocr.message} 직접 고쳐 적어도 됩니다.</p>}
                {ocr.status === 'done' && <p>이미지에서 읽은 문장이에요. 틀린 글자가 있으면 고쳐 주세요.</p>}
                <button
                  type="button"
                  onClick={() => readImage({ force: true })}
                  className="btn-ghost mt-2 !px-3 !py-1.5 text-xs"
                >
                  <Sparkles size={13} strokeWidth={1.5} />
                  {ocr.status === 'idle' ? '이미지에서 문장 읽기' : '다시 읽기'}
                </button>
              </>
            )}
          </div>
        </section>
      )}

      {/* 인용 */}
      <section className="mt-8">
        <div className="flex items-baseline justify-between gap-3">
          <span className="label">인용</span>
          {payload.text && quote !== payload.text && (
            <button
              type="button"
              onClick={() => setQuote(payload.text)}
              className="flex items-center gap-1 text-xs text-ink-500 hover:text-ink-900 dark:text-ink-400 dark:hover:text-ink-100 font-sans"
              title="자동으로 뗀 줄까지 받은 글 그대로 되돌립니다"
            >
              <RotateCcw size={12} strokeWidth={1.5} />
              받은 글 그대로
            </button>
          )}
        </div>
        <textarea
          value={quote}
          onChange={e => setQuote(e.target.value)}
          rows={6}
          lang="ko"
          spellCheck={false}
          placeholder={ocr.status === 'reading' ? '이미지에서 읽는 중…' : '책에서 옮겨 적고 싶은 문장'}
          className="field mt-2 resize-y font-display italic text-base leading-relaxed"
        />
      </section>

      {/* 쪽수 */}
      <section className="mt-6">
        <label className="flex items-center gap-3">
          <span className="label">쪽</span>
          <NumberInput
            min="1"
            value={page}
            onChange={e => setPage(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                e.preventDefault();
                handleSave();
              }
            }}
            placeholder={lastPage ? `p.${lastPage}` : 'p.'}
            aria-label="쪽수"
            className="!w-24 !px-2 text-center"
          />
          {scale.useEbook && (
            <span className="text-xs text-ink-500 dark:text-ink-400 font-sans">전자책 쪽수로</span>
          )}
        </label>
      </section>

      {/* 내 생각 */}
      <section className="mt-6">
        <span className="label">내 생각 · 선택</span>
        <textarea
          value={thought}
          onChange={e => setThought(e.target.value)}
          rows={3}
          lang="ko"
          spellCheck={false}
          placeholder="이 문장에 대해 떠오른 생각 (인용과 함께 저장돼요)"
          className="field mt-2 resize-y text-sm leading-relaxed"
        />
      </section>

      <div className="mt-8 flex items-center gap-2">
        <button type="button" onClick={handleCancel} className="btn-ghost text-xs">취소</button>
        <button
          type="button"
          onClick={handleSave}
          disabled={!canSave}
          className="btn-primary flex-1 text-sm disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {saving ? '저장 중...' : '인용 저장'}
        </button>
      </div>
      {closable && (
        <p className="mt-3 text-center text-xs text-ink-500 dark:text-ink-400 font-sans">
          저장하면 이 창을 닫고 보던 앱으로 돌아가요
        </p>
      )}
    </div>
  );
};

export default ShareReceive;
